export interface NormalizedText {
  value: string;
  /** For every normalized UTF-16 code unit, the originating original span. */
  originalStarts: number[];
  originalEnds: number[];
}

const COMBINING_MARK = /^\p{M}$/u;
const VARIATION_SELECTOR = /^(?:\uFE0E|\uFE0F)$/u;

/**
 * Normalize grapheme-like units so a decomposed accent maps back to the whole
 * original grapheme. This is deliberately conservative: offsets always point
 * into the original string, never a normalized copy.
 */
function normalizationUnits(input: string): Array<{ value: string; start: number; end: number }> {
  const units: Array<{ value: string; start: number; end: number }> = [];
  let index = 0;
  let current = "";
  let currentStart = 0;
  let joinNext = false;

  for (const codePoint of input) {
    const start = index;
    index += codePoint.length;
    const continues = current.length > 0 && (COMBINING_MARK.test(codePoint) || VARIATION_SELECTOR.test(codePoint) || joinNext);
    if (!continues) {
      if (current.length > 0) units.push({ value: current, start: currentStart, end: start });
      current = codePoint;
      currentStart = start;
    } else {
      current += codePoint;
    }
    joinNext = codePoint === "\u200D";
  }
  if (current.length > 0) units.push({ value: current, start: currentStart, end: input.length });
  return units;
}

export function normalizeWithIndex(input: string, caseSensitive = false): NormalizedText {
  let value = "";
  const originalStarts: number[] = [];
  const originalEnds: number[] = [];
  for (const unit of normalizationUnits(input)) {
    const normalized = caseSensitive ? unit.value.normalize("NFKC") : unit.value.normalize("NFKC").toLocaleLowerCase("und");
    value += normalized;
    for (let i = 0; i < normalized.length; i += 1) {
      originalStarts.push(unit.start);
      originalEnds.push(unit.end);
    }
  }
  return { value, originalStarts, originalEnds };
}

export function normalizeForMatch(value: string, caseSensitive = false): string {
  return caseSensitive ? value.normalize("NFKC") : value.normalize("NFKC").toLocaleLowerCase("und");
}

export function originalSpan(normalized: NormalizedText, start: number, end: number): { startUtf16: number; endUtf16: number } | undefined {
  if (start < 0 || end <= start || end > normalized.value.length) return undefined;
  return {
    startUtf16: normalized.originalStarts[start]!,
    endUtf16: normalized.originalEnds[end - 1]!,
  };
}

function codePointBefore(value: string, index: number): string | undefined {
  if (index <= 0) return undefined;
  const last = value.charCodeAt(index - 1);
  if (last >= 0xDC00 && last <= 0xDFFF && index >= 2) {
    const first = value.charCodeAt(index - 2);
    if (first >= 0xD800 && first <= 0xDBFF) return value.slice(index - 2, index);
  }
  return value.slice(index - 1, index);
}

function codePointAt(value: string, index: number): string | undefined {
  if (index < 0 || index >= value.length) return undefined;
  const codePoint = value.codePointAt(index);
  return codePoint === undefined ? undefined : String.fromCodePoint(codePoint);
}

function isWordCodePoint(value: string | undefined): boolean {
  return value !== undefined && /^[\p{L}\p{N}_]$/u.test(value);
}

export function hasWordCodePointBefore(value: string, index: number): boolean {
  return isWordCodePoint(codePointBefore(value, index));
}

export function hasWordCodePointAt(value: string, index: number): boolean {
  return isWordCodePoint(codePointAt(value, index));
}
